import _classnames from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';
import Checkbox from '../form/checkbox/checkbox';
import ComplexIcon from '../icons/ComplexIcon/ComplexIcon';
import Icon from '../icons/Icon/Icon';

/**
 * This is the item component rendered inside the `Dropdown` component. Stories do not show hover
 * state because this is handled with callback functions in the `Dropdown` parent component.
 */
function DropdownItem({
  active,
  buttonid,
  caption,
  checkboxProps,
  children,
  closeAfterClick,
  closePanel,
  customItem,
  destructive,
  disabled,
  domId,
  handleRowHover,
  hasCheckbox,
  header,
  iconLeft,
  iconLeftGivenColor,
  iconRight,
  id,
  isLoading,
  onClick,
  onCheckboxClick,
  small,
  supportingLabel,
}) {
  const classes = _classnames('Dropdown__item', {
    'Dropdown__item--active': active,
    'Dropdown__item--destructive': destructive,
    'Dropdown__item--disabled': disabled,
  });

  const captionClasses = _classnames('Dropdown__item__caption', {
    Dropdown__item__headercaption: header,
  });

  let captionBlock = '';

  if (caption.length > 0) {
    captionBlock = <div className={captionClasses}>{caption}</div>;
  }

  if (header) {
    return (
      <li className={_classnames('Dropdown__item__sectionheader', classes)}>
        <div
          className={_classnames('Dropdown__item__headertext', {
            'Dropdown__item__headertext--small': small,
          })}
        >
          {children}
        </div>
        {captionBlock}
      </li>
    );
  }

  const buttonClasses = _classnames('Button--unstyled', 'Dropdown__item__button', classes);

  const contentClasses = _classnames('Dropdown__item__text', {
    'Dropdown__item__text--has-icon-left': iconLeft || hasCheckbox,
  });

  let iconRightColor = '#0a1435';
  let iconLeftColor = '#8008f7';
  if (iconLeftGivenColor) {
    iconLeftColor = iconLeftGivenColor;
  }
  if (destructive && disabled) {
    iconLeftColor = '#ffb8b8';
    iconRightColor = '#ffb8b8';
  } else if (destructive) {
    iconLeftColor = '#db0000';
    iconRightColor = '#db0000';
  } else if (disabled) {
    iconLeftColor = '#969fb1';
    iconRightColor = '#969fb1';
  }

  let supportingLabelValue = supportingLabel;
  if (supportingLabel && iconRight) {
    supportingLabelValue = null;
  }

  let iconLeftValue = iconLeft;
  if (iconLeft && hasCheckbox) {
    iconLeftValue = null;
  }

  return (
    <li className={classes} id={domId}>
      {customItem ? (
        children
      ) : (
        <button
          className={buttonClasses}
          disabled={disabled}
          onClick={(e) => {
            e.preventDefault();
            if (hasCheckbox) {
              onCheckboxClick(id);
            }
            if (onClick) {
              onClick(id);
            }
            if (closeAfterClick) {
              closePanel();
            }
          }}
          onMouseEnter={() => handleRowHover(id)}
          onMouseMove={() => handleRowHover(id)}
          onMouseLeave={() => handleRowHover(null)}
          type="button"
          id={buttonid}
        >
          <div className="Dropdown__item__content">
            {iconLeftValue && (
              <Icon
                name={iconLeftValue}
                size="minor"
                svgProps={{ style: { fill: iconLeftColor } }}
              />
            )}
            {hasCheckbox && (
              <Checkbox
                checked={checkboxProps.checked}
                disabled={checkboxProps.disabled}
                indeterminate={checkboxProps.indeterminate}
                selected={active}
              />
            )}
            <div className={contentClasses}>{children}</div>

            <div className="Dropdown__item__supporting-label">
              {supportingLabelValue && (
                <div className="Dropdown__item__supporting-text">{supportingLabelValue}</div>
              )}
              {!isLoading && iconRight && (
                <Icon
                  name={iconRight}
                  size="minor"
                  svgProps={{ style: { fill: iconRightColor } }}
                />
              )}
              {!!isLoading && <ComplexIcon classes="Dropdown__item__spinner" name="spinner" />}
            </div>
          </div>
          {captionBlock}
        </button>
      )}
    </li>
  );
}

DropdownItem.propTypes = {
  /**
   * Renders the item in an active state (managed by `Dropdown` component)
   */
  active: PropTypes.bool,
  buttonid: PropTypes.string,
  /**
   * Optional caption text below the item's main text or node (`children`)
   */
  caption: PropTypes.string,
  /**
   * An object containing any of three keys with boolean values for each: `checked`, `disabled`,
   * `indeterminate`. Must be included if `hasCheckbox` is set to `true`. (Included automatically
   * when items are rendered from Dropdown component)
   */
  checkboxProps: PropTypes.shape({
    checked: PropTypes.bool,
    disabled: PropTypes.bool,
    indeterminate: PropTypes.bool,
  }),
  /**
   * The main text or node contained in the item
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  /**
   * Set to `true` if the `DropdownPanel` should be closed after the item is clicked
   */
  closeAfterClick: PropTypes.bool,
  /**
   * A method passed in from the `Dropdown` component which can be called from `DropdownItem` to
   * close the `DropdownPanel`
   */
  closePanel: PropTypes.func,
  /**
   * Set to true when creating a custom item with specific nodes passed in as children. The only
   * `DropdownItem` props that interact with a custom item are: `active`, `disabled`,
   * `destructive`, `domId`
   */
  customItem: PropTypes.bool,
  /**
   * Set to `true` to show item in destructive state (red)
   */
  destructive: PropTypes.bool,
  disabled: PropTypes.bool,
  /**
   * Used as the component's `id` value (passed in automatically from `DropdownSection` when
   * `Dropdown` is rendered). This value is used to track the location of the item within the
   * panel to ensure the active item is always visible.
   */
  domId: PropTypes.string,
  /**
   * Set to `true` if the item should be rendered with a checkbox
   */
  hasCheckbox: PropTypes.bool,
  /**
   * A method passed in from the `Dropdown` component which sets the item's active status based on
   * any `onMouseEnter`, `onMouseMove`, or `onMouseLeave` events
   */
  handleRowHover: PropTypes.func,
  /**
   * Set to `true` if the item should be styled as a header. Header items will not receive any
   * active status and are skipped when navigating through the list by keyboard. Headers are not
   * related to how `DropdownSection`s are defined.
   */
  header: PropTypes.bool,
  /**
   * Include an `Icon` name to be used as the left side icon within the item. This value gets ignored
   * if `hasCheckbox` is set to `true`.
   */
  iconLeft: PropTypes.string,
  /**
   * Include a hex value as a string to override the default color for the left icon. This is not
   * applied when the item is in a `disabled` and/or `destructive` state.
   */
  iconLeftGivenColor: PropTypes.string,
  /**
   * Include an `Icon` name to be used as the right side icon within the item
   */
  iconRight: PropTypes.string,
  /**
   * Automatically populated by the `Dropdown` component when the items are rendered. Corresponds
   * to the index of the item within the `Dropdown` component's 'items' array. Value is utilized
   * during item interactions within several callback functions (`onClick`, `onCheckboxClick`,
   * `handleRowHover`)
   */
  id: PropTypes.number.isRequired,
  /**
   * Loading state shows the spinner icon on the right side of the item. Overrides any `iconRight` or
   * `supportingLabel` while in loading state.
   */
  isLoading: PropTypes.bool,
  /**
   * Callback function to run when item is clicked
   */
  onClick: PropTypes.func,
  /**
   * Callback function to run when a checkbox item is clicked
   */
  onCheckboxClick: PropTypes.func,
  /**
   * Set to true to apply `small` styles to the item
   */
  small: PropTypes.bool,
  /**
   * Small supporting information rendered on the right side of the item. Value will be ignored if
   * there is also an `iconRight` value indicated.
   */
  supportingLabel: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
};

DropdownItem.defaultProps = {
  active: false,
  caption: '',
  children: '',
  disabled: false,
  handleRowHover: () => {},
  hasCheckbox: false,
  header: false,
  iconLeft: null,
  iconRight: null,
  onClick: null,
  supportingLabel: '',
  checkboxProps: {
    checked: false,
    disabled: false,
    indeterminate: false,
  },
  closeAfterClick: false,
  closePanel: undefined,
  customItem: false,
  destructive: false,
  domId: undefined,
  iconLeftGivenColor: undefined,
  isLoading: false,
  onCheckboxClick: undefined,
  small: false,
};

export default DropdownItem;
